import { useCallback, useEffect, useRef, useState } from 'react'
import { isBoolean, isObject } from 'query-types'
import isEqual from 'lodash/isEqual'

function isJSON(val) {
  try {
    const obj = JSON.parse(val)
    if (obj && typeof obj === 'object' && obj !== null) {
      return true
    }
  } catch (err) {
    return false
  }

  return false
}

export function useLocalStorage(key, initialValue = {}) {
  const initialValueRef = useRef(initialValue)

  useEffect(() => {
    initialValueRef.current = initialValue
  }, [initialValue])

  const readStoredValue = useCallback(() => {
    const stored = localStorage.getItem(key)
    if (stored === null || stored === undefined) {
      return initialValueRef.current
    }

    if (isJSON(stored)) {
      const parsed = JSON.parse(stored)
      const defaults = initialValueRef.current
      if (isObject(parsed) && isObject(defaults)) {
        return { ...defaults, ...parsed }
      }
      return parsed
    }

    if (isBoolean(stored)) {
      return stored === 'true'
    }

    return stored
  }, [key])

  const [value, setValue] = useState(readStoredValue)

  const setCfg = useCallback(
    (val) => {
      const stored = localStorage.getItem(key)
      const currentState = isJSON(stored) ? JSON.parse(stored) : stored

      const merged =
        !!currentState && isObject(currentState)
          ? { ...currentState, ...val }
          : isBoolean(val)
          ? val === 'true'
          : val

      setValue(merged)

      localStorage.setItem(key, JSON.stringify(merged))
    },
    [key, setValue],
  )

  useEffect(() => {
    const nextValue = readStoredValue()
    if (!isEqual(value, nextValue)) {
      setValue(nextValue)
      localStorage.setItem(key, JSON.stringify(nextValue))
    }
  }, [key, readStoredValue, setValue, value])

  return [value, setCfg]
}
